//import { ICategory } from 'Models/Category';
import { TypeOf, array, boolean, coerce, date, nativeEnum, object, string, union, z, instanceof as zInstanceof } from 'zod';
import { ISelectOption } from 'Interfaces/Utils';
import { IOperation } from 'Models/Operation';
import { id } from 'date-fns/locale';

// Form component interface

export interface IOperationFormProps {
  companies: ISelectOption<string>[];
  pointsOfSale: ISelectOption<string>[];
  documentType: ISelectOption<string>[];
  profesionalActivities: ISelectOption<string>[];
  apiError: any;
  currentOperation?: IOperation;
  onSubmit: (formData: FormData) => void;
};


// Form fields

export const FIELDS = {

  AMOUNT: {
    FIELD: 'amount',
    ERROR: 'Cantidad de la operación obligatorio',
  },
  OPERATION_DATE: {
    FIELD: 'operationDate',
    ERROR_INVALID: 'La fecha de la operación no es válida',
  },
  POINT_OF_SALE: {
    FIELD: 'pointOfSaleId',
    ERROR: 'Punto de venta obligatorio',
  },
  COMPANY: {
    FIELD: 'companyId',
    ERROR: 'Empresa obligatoria',
  },
  DOCUMENT_TYPE: {
    FIELD: 'documentTypeId',
    ERROR: 'Tipo de documento obligatorio',
  },
  NATIONALITY: {
    FIELD: 'nationality',
    ERROR: 'Nacionalidad obligatoria',
  },
  PROVINCE: {
    FIELD: 'province',
    ERROR: 'Provincia obligatoria',
  },
  STATUS: {
    FIELD: 'status',
    ERROR: 'Estado obligatorio',
  },
  CLIENT_NAME: {
    FIELD: 'clientName',
    ERROR: 'Nombre del cliente obligatorio',
  },
  DOCUMENT_NUMBER: {
    FIELD: 'documentNumber',
    ERROR: 'Número de documento obligatorio',
  },
  DOCUMENT_EXPIRATION_DATE: {
    FIELD: 'documentExpirationDate',
    ERROR: 'Fecha de vencimiento del documento obligatorio',
  },
  CLIENT_BIRTH_DATE: {
    FIELD: 'clientBirthDate',
    ERROR: 'Fecha de nacimiento del cliente obligatorio',
  },
  LEGAL_FUNDS: {
    FIELD: 'legalFunds',
    ERROR: 'Fondos legales obligatorios',
  },
  PROFESIONAL_ACTIVITY: {
    FIELD: 'professionalActivity',
    ERROR: 'Actividad profesional obligatoria',
  },
  SALES_CHANNEL: {
    FIELD: 'salesChannel',
    ERROR: 'Canal de venta',
  },
  PUBLIC_OFFICE: {
    FIELD: 'publicOffice',
    ERROR: 'Cargo público obligatorio',
  },
  PURCHASE_PURPOSE: {
    FIELD: 'purchasePurpose',
    ERROR: 'Propósito de la compra obligatorio',
  },
  CLIENT_ADDRESS: {
    FIELD: 'clientAddress',
    ERROR: 'Dirección del cliente obligatoria',
  },
  CLIENT_EMAIL: {
    FIELD: 'clientEmail',
    ERROR: 'Email del cliente obligatorio',
  },
  DOCUMENT_OBVERSE: {
    FIELD: 'documentObverse',
    ERROR: 'Documento obligatorio',
  },
  DOCUMENT_REVERSE: {
    FIELD: 'documentReverse',
    ERROR: 'Documento obligatorio',
  },
  SIGNATURE:{
    FIELD: 'signature',
    ERROR: 'Firma obligatoria',
  },
  SALES_DOCUMENT:{
    FIELD: 'salesDocument',
    ERROR: 'Documento de venta obligatorio',
  },
  LEGAL_TERMS: {
    FIELD: 'legalTerms',
    ERROR: 'Debe aceptar los términos legales',
  },
  FUNDS_ORIGIN_COUNTRY: {
    FIELD: 'fundsOriginCountry',
    ERROR: 'País de origen de los fondos obligatorio',
  },
  ID: {
    FIELD: 'id',
    ERROR: 'ID obligatorio',
  },
  ID_NEO_CHECK: {
    FIELD: 'idNeoCheck',
    ERROR: 'ID obligatorio',
  },
};

// Form validation schema

export const operationFormSchema = object({
  [FIELDS.ID.FIELD]: string().optional(),
  [FIELDS.AMOUNT.FIELD]: z.preprocess((val) => parseFloat(String(val)), z.number().min(1, { message: FIELDS.AMOUNT.ERROR })),
  [FIELDS.OPERATION_DATE.FIELD]: coerce 
  .date({errorMap:()=>({message: FIELDS.OPERATION_DATE.ERROR_INVALID})
  }).optional(),
  [FIELDS.POINT_OF_SALE.FIELD]: z
  .string()
  .transform((val) => val === "" ? undefined : val)
  .optional(),

  [FIELDS.SALES_CHANNEL.FIELD]: z.string().optional(),
  [FIELDS.COMPANY.FIELD]: string().min(1, { message: FIELDS.COMPANY.ERROR }),
  [FIELDS.DOCUMENT_TYPE.FIELD]: string().min(1, { message: FIELDS.DOCUMENT_TYPE.ERROR }),
  [FIELDS.NATIONALITY.FIELD]: string().optional(),
  [FIELDS.STATUS.FIELD]: string().min(1, { message: FIELDS.STATUS.ERROR }),
  [FIELDS.PROVINCE.FIELD]: string().optional(),
  [FIELDS.CLIENT_NAME.FIELD]: string().optional(),
  [FIELDS.DOCUMENT_NUMBER.FIELD]: string().optional(),
  [FIELDS.CLIENT_EMAIL.FIELD]: string().optional(),
  [FIELDS.DOCUMENT_OBVERSE.FIELD]: union([string(), zInstanceof(File)]).optional(),
  [FIELDS.DOCUMENT_REVERSE.FIELD]: union([string(), zInstanceof(File)]).optional(),
  [FIELDS.SIGNATURE.FIELD]: union([string(), zInstanceof(File)]).optional(),
  [FIELDS.DOCUMENT_EXPIRATION_DATE.FIELD]: string().optional(),
  [FIELDS.CLIENT_BIRTH_DATE.FIELD]: string().optional(),
  [FIELDS.LEGAL_FUNDS.FIELD]: boolean().optional(),
  [FIELDS.PROFESIONAL_ACTIVITY.FIELD]: string().optional(),
  [FIELDS.PUBLIC_OFFICE.FIELD]: string().optional(),
  [FIELDS.PURCHASE_PURPOSE.FIELD]: string().optional(),
  [FIELDS.CLIENT_ADDRESS.FIELD]: string().optional(),
  [FIELDS.LEGAL_TERMS.FIELD]: boolean().optional(),
  [FIELDS.FUNDS_ORIGIN_COUNTRY.FIELD]: string().optional(),
  [FIELDS.ID_NEO_CHECK.FIELD]: string().optional(),

})
.refine(
  (data) =>
    data.salesChannel !== "ECOMMERCE" || !(typeof data.pointOfSaleId === 'string' && data.pointOfSaleId.length > 0),
  {
    message: FIELDS.POINT_OF_SALE.ERROR,
    path: [FIELDS.POINT_OF_SALE.FIELD],
  }
);

// Form values interface

export type IOperationForm = TypeOf<typeof operationFormSchema>;

// Default values function

export const initialValues = (operation: IOperation| undefined): IOperationForm => {
  const newsForm: IOperationForm = {
    amount: operation?.amount || 0,
    operationDate: operation?.operationDate || undefined,
    pointOfSaleId: operation?.pointOfSaleId || "",
    company: operation?.companyId || "",
    documentType: operation?.documentTypeId || "",
    nationality: operation?.nationality || "",
    province: operation?.province || "",
    status: operation?.status || "",
    clientName: operation?.clientName || "",
    documentNumber: operation?.documentNumber || "",
    clientEmail: operation?.clientEmail || "",
    documentObverse: operation?.documentObverse || "",
    documentReverse: operation?.documentReverse || "",
    signature: operation?.signature || "",
    documentExpirationDate: operation?.documentExpirationDate || "",
    clientBirthDate: operation?.clientBirthDate || "",
    legalFunds: operation?.legalFunds || false,
    profesionalActivity: operation?.professionalActivity || "",
    publicOffice: operation?.publicOffice || "",
    purchasePurpose: operation?.purchasePurpose || "",
    clientAddress: operation?.clientAddress || "",
    legalTerms: operation?.acceptedLegalTerms || false,
    fundsOriginCountry: operation?.fundsOriginCountry || "",
    id: operation?.id || "",
    idNeoCheck: operation?.idNeoCheck || "",
    salesChannel: operation?.salesChannel || "",
  };

  return newsForm;
};